import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import './CSS/Login.css';

const COST_PER_VERIFY = 1;

const CamSlipcheck = ({ history, location }) => {
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState(0);
  const [isUserLoaded, setIsUserLoaded] = useState(false);

  // === Load user info (router → localStorage → backend fallback) ===
  useEffect(() => {
    const state = location && location.state;
    const localEmail = localStorage.getItem('email');
    const localBalance = localStorage.getItem('balance');

    if (state && state.email) {
      console.log('✅ Loaded from router:', state);
      setEmail(state.email);
      setBalance(Number(state.balance || 0));
      localStorage.setItem('email', state.email);
      localStorage.setItem('balance', Number(state.balance || 0));
      setIsUserLoaded(true);
    } else if (localEmail) {
      console.log('✅ Loaded from localStorage');
      setEmail(localEmail);
      setBalance(Number(localBalance || 0));
      setIsUserLoaded(true);
    } else {
      console.log('🔁 Fetching from backend /user-details...');
      fetchUserDetails();
    }
  }, [location]);

  const fetchUserDetails = async () => {
    try {
      const res = await fetch('/user-details');
      const data = await res.json();
      setEmail(data.email || '');
      setBalance(Number(data.balance || 0));
      localStorage.setItem('email', data.email || '');
      localStorage.setItem('balance', Number(data.balance || 0));
      setIsUserLoaded(true);
    } catch {
      setMessage('⚠️ Failed to fetch user data.');
      setIsUserLoaded(true);
    }
  };

  // === Initialize QR Scanner ===
  useEffect(() => {
    if (!isUserLoaded) return;

    const scanner = new Html5QrcodeScanner(
      'qr-reader',
      { fps: 10, qrbox: { width: 250, height: 250 } },
      false
    );

    scanner.render(onScanSuccess, onScanError);

    function onScanSuccess(decodedText) {
      if (isProcessing) return;
      setMessage('QR Code detected! Verifying...');
      verifySlip(decodedText);
      scanner.clear();
    }

    function onScanError(err) {
      // optional: ignore scanning noise
    }

    return () => {
      scanner.clear().catch(() => {});
    };
  }, [isUserLoaded, isProcessing]);

  // === Verify Slip ===
  const verifySlip = async (qrData) => {
    if (!qrData) return;
    if (!isUserLoaded) {
      setMessage('⏳ Please wait... loading your balance.');
      return;
    }

    if (balance < COST_PER_VERIFY) {
      setMessage(`❌ Not enough credits (you have ${balance.toFixed(2)}).`);
      return;
    }

    // ✅ Add confirmation alert before deduction
    const confirm = window.confirm(
      `Verify this slip now?\nThis will deduct ${COST_PER_VERIFY} credit.\n` +
        `Current balance: ${balance.toFixed(2)} → After: ${(balance - COST_PER_VERIFY).toFixed(2)}`
    );
    if (!confirm) return;

    setIsProcessing(true);
    setApiData(null);
    setTimestamp(null);
    setMessage('🔍 Verifying slip via EasySlip...');

    try {
      // === Step 1: Verify slip first ===
      const res = await fetch('/verify-slipcam', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qrData }),
      });

      const result = await res.json();
      setApiData(result.details || null);
      setTimestamp(result.timestamp || null);

      if (res.ok && result.isReal) {
        setIsSuccess(true);
        setMessage('✅ Verified: This slip is REAL and issued by the bank.');

        // === Step 2: Deduct credit AFTER success ===
        const newBalance = balance - COST_PER_VERIFY;
        const balanceRes = await fetch('/update-balance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, newBalance }),
        });

        if (balanceRes.ok) {
          setBalance(newBalance);
          localStorage.setItem('balance', newBalance);
          localStorage.setItem('email', email);
        } else {
          console.warn('⚠️ Failed to update balance on backend.');
        }
      } else {
        setIsSuccess(false);
        setMessage(`❌ Slip not verified: ${result.message || 'Unknown error.'}`);
      }
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setMessage('⚠️ Error verifying slip.');
    } finally {
      setIsProcessing(false);
    }
  };

  // === Logout Function ===
  const handleLogout = () => {
    localStorage.removeItem('email');
    localStorage.removeItem('balance');
    window.location.reload(); // Refreshes to reset app to login
  };

  const formatThaiDate = (isoDate) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const renderReadableApiData = (data) => {
    if (!data) return null;

    const readable = {
      'Verification Result': isSuccess ? '✅ Real Slip' : '❌ Fake Slip',
      'Payment Timestamp (ISO)': timestamp || 'N/A',
      'Payment Time (Local Thai)': timestamp ? formatThaiDate(timestamp) : 'N/A',
      'Bank Name': data.receiver?.bank?.name || 'N/A',
      'Sender Name': data.sender?.account?.name?.th || 'N/A',
      'Receiver Name': data.receiver?.account?.name?.th || 'N/A',
      'Receiver Account': data.receiver?.account?.proxy?.account || 'N/A',
      'Amount': data.amount?.amount ? `฿${data.amount.amount.toFixed(2)}` : 'N/A',
      'Transaction ID': data.transRef || 'N/A',
    };

    return (
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '10px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
          padding: '20px',
          marginTop: '20px',
          textAlign: 'left',
          width: '100%',
        }}
      >
        <h3>🧾 Verification Details</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {Object.entries(readable).map(([key, value]) => (
            <li key={key} style={{ marginBottom: '8px' }}>
              <strong>{key}:</strong> <span>{value}</span>
            </li>
          ))}
        </ul>

        <h4 style={{ marginTop: '15px' }}>📦 Full API JSON:</h4>
        <pre
          style={{
            backgroundColor: '#f8f8f8',
            padding: '10px',
            borderRadius: '8px',
            fontSize: '13px',
            overflowX: 'auto',
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        minHeight: '100vh',
        background: '#f2f4f8',
      }}
    >
      <div
        style={{
          maxWidth: '500px',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        }}
      >
        <img src={logo} alt="Logo" style={{ width: '200px', marginBottom: '20px' }} />
        <h2>Bank Slip Verification Service</h2>
        <p>Scan the QR code on your bank slip to verify authenticity (−1 credit on success).</p>

        <div className="mb-2">
          <strong>Email:</strong> {email || '—'} &nbsp;|&nbsp;
          <strong>Balance:</strong> {balance.toFixed(2)} credits
        </div>

        {/* 🔹 Logout Button */}
        <button
          onClick={handleLogout}
          className="btn btn-outline-dark btn-sm mb-3"
          style={{ width: '100%', marginTop: '10px' }}
        >
          🔒 Logout
        </button>

        {!isUserLoaded ? (
          <p>⏳ Loading your account info...</p>
        ) : (
          <div id="qr-reader" style={{ width: '100%', marginTop: '20px' }}></div>
        )}

        {message && (
          <p
            style={{
              marginTop: '15px',
              color: isSuccess ? 'green' : 'red',
              fontWeight: 'bold',
            }}
          >
            {message}
          </p>
        )}

        {renderReadableApiData(apiData)}
      </div>

      <div style={{ textAlign: 'center', marginTop: 'auto', paddingBottom: '20px' }}>
        <p style={{ fontSize: '14px', marginBottom: '10px' }}>Powered by</p>
        <img src={logo2} alt="Logo2" style={{ width: '100px', opacity: 0.8 }} />
        <div>New Wave Consulting Group</div>
      </div>
    </div>
  );
};

export default withRouter(CamSlipcheck);
